"use client";

import { useT } from "@/lib/i18n/context";
import type { WShopNewProductsContent } from "@/lib/api/cms-window-content";
import { ProductCard } from "@/components/ui/ProductCard/ProductCard";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import Link from "next/link";
import { wShopNewProductsTranslations } from "./WShopNewProducts.i18n";
import styles from "./WShopNewProducts.module.css";

/**
 * WShopNewProducts - okno "nowosci w sklepie".
 *
 * Grid kafelkow produktow (reuse `<ProductCard />` z ui/), naglowek sekcji z
 * tytulem instancji okna + opcjonalny przycisk "zobacz wszystkie" gdy admin
 * ustawil `config[list_url]`. Fallback title z i18n gdy admin nie nazwal
 * instancji (`content.title` puste).
 *
 * Rendering identyczny z pozostalymi oknami sklepowymi - roznica wylacznie w
 * warstwie danych (backend filtruje po fladze `new=1` zaznaczonej przez
 * administratora). Rozne od `WShopLatestProducts`, ktory sortuje po `add_date
 * DESC` (fizycznie najnowsze), a nie filtruje po fladze administratora.
 *
 * Data source: `W_SHOP_NEW_PRODUCTS_CONTROLLER::getApiContent()`.
 */
export function WShopNewProducts({
  content,
  title,
}: WShopNewProductsProps) {
  const t = useT(wShopNewProductsTranslations);

  if (!content) return null;

  const headingText = title || content.title || t.title;
  const showAllHref = content.href ? extractInternalHref(content.href) : null;

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <h2 className={styles.heading}>{headingText}</h2>
        {content.href && (
          showAllHref !== null ? (
            <Link href={showAllHref} className={styles.showAllBtn}>
              {t.showAll}
            </Link>
          ) : (
            <a href={content.href} className={styles.showAllBtn}>
              {t.showAll}
            </a>
          )
        )}
      </div>

      {content.items.length === 0 ? (
        <div className={styles.empty}>{t.noProducts}</div>
      ) : (
        <ul className={styles.productList}>
          {content.items.map((item) => (
            <ProductCard
              key={item.id}
              item={item}
              showPrices={content.show_prices}
              currency={content.currency}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export type WShopNewProductsProps = {
  content: WShopNewProductsContent | null;
  title: string;
};
